import React, { useState, useEffect } from "react";
import {
  ClockIcon,
  CalendarIcon,
  PlusIcon,
  TrashIcon,
  DuplicateIcon,
  CheckIcon,
  XIcon,
} from "@heroicons/react/outline";

const WorkingHoursConfig = ({
  workingHours = {},
  holidays = [],
  onChange,
  viewOnly = false,
}) => {
  const days = [
    "monday",
    "tuesday",
    "wednesday",
    "thursday",
    "friday",
    "saturday",
    "sunday",
  ];

  const defaultHours = {
    open: "10:00",
    close: "20:00",
    isOpen: true,
    breakStart: "",
    breakEnd: "",
  };

  const [hours, setHours] = useState(() => {
    const initialHours = {};
    days.forEach((day) => {
      initialHours[day] = workingHours[day] || { ...defaultHours };
    });
    return initialHours;
  });

  const [holidayList, setHolidayList] = useState(holidays);
  const [showHolidayForm, setShowHolidayForm] = useState(false);
  const [newHoliday, setNewHoliday] = useState({
    date: "",
    name: "",
    isOpen: false,
    specialHours: {
      open: "",
      close: "",
    },
  });
  const [applyToAll, setApplyToAll] = useState(false);
  const [selectedTemplate, setSelectedTemplate] = useState("");

  useEffect(() => {
    if (!viewOnly && onChange) {
      onChange({ workingHours: hours, holidays: holidayList });
    }
  }, [hours, holidayList]);

  const updateDay = (day, field, value) => {
    if (viewOnly) return;

    const updatedHours = {
      ...hours,
      [day]: {
        ...hours[day],
        [field]: value,
      },
    };

    if (applyToAll && field !== "isOpen") {
      days.forEach((d) => {
        if (hours[d].isOpen) {
          updatedHours[d] = {
            ...updatedHours[d],
            [field]: value,
          };
        }
      });
    }

    setHours(updatedHours);
  };

  const toggleDay = (day) => {
    if (viewOnly) return;
    setHours((prev) => ({
      ...prev,
      [day]: {
        ...prev[day],
        isOpen: !prev[day].isOpen,
      },
    }));
  };

  const copyHours = (fromDay, toDay) => {
    if (viewOnly) return;
    setHours((prev) => ({
      ...prev,
      [toDay]: { ...prev[fromDay] },
    }));
  };

  const applyTemplate = (template) => {
    if (viewOnly) return;

    switch (template) {
      case "weekdays":
        const mondayHours = hours.monday;
        ["tuesday", "wednesday", "thursday", "friday"].forEach((day) => {
          setHours((prev) => ({
            ...prev,
            [day]: { ...mondayHours },
          }));
        });
        break;
      case "weekend":
        const saturdayHours = hours.saturday;
        setHours((prev) => ({
          ...prev,
          sunday: { ...saturdayHours },
        }));
        break;
      case "24x7":
        days.forEach((day) => {
          setHours((prev) => ({
            ...prev,
            [day]: {
              open: "00:00",
              close: "23:59",
              isOpen: true,
              breakStart: "",
              breakEnd: "",
            },
          }));
        });
        break;
      case "standard":
        days.forEach((day) => {
          const isWeekend = day === "sunday";
          setHours((prev) => ({
            ...prev,
            [day]: {
              open: "10:00",
              close: "20:00",
              isOpen: !isWeekend,
              breakStart: "",
              breakEnd: "",
            },
          }));
        });
        break;
      default:
        break;
    }
  };

  const addHoliday = () => {
    if (viewOnly) return;
    if (newHoliday.date && newHoliday.name) {
      const holiday = {
        ...newHoliday,
        id: Date.now(),
      };
      setHolidayList([...holidayList, holiday]);
      setNewHoliday({
        date: "",
        name: "",
        isOpen: false,
        specialHours: { open: "", close: "" },
      });
      setShowHolidayForm(false);
    }
  };

  const removeHoliday = (id) => {
    if (viewOnly) return;
    setHolidayList(holidayList.filter((h) => h.id !== id));
  };

  const getDayDisplayName = (day) => {
    return day.charAt(0).toUpperCase() + day.slice(1);
  };

  const isToday = (day) => {
    // Fixed: Changed 'lowercase' to 'long' and convert to lowercase manually
    const today = new Date()
      .toLocaleDateString("en-US", { weekday: "long" })
      .toLowerCase();
    return today === day;
  };

  const formatTime = (time) => {
    if (!time) return "--:--";
    return time;
  };

  return (
    <div className="space-y-6">
      {/* Header with Templates */}
      <div className="flex items-center justify-between">
        <h3 className="text-lg font-semibold flex items-center">
          <ClockIcon className="h-5 w-5 mr-2" />
          Working Hours
        </h3>

        {!viewOnly && (
          <div className="flex gap-2">
            <select
              value={selectedTemplate}
              onChange={(e) => {
                setSelectedTemplate(e.target.value);
                if (e.target.value) {
                  applyTemplate(e.target.value);
                }
              }}
              className="text-sm px-3 py-1 border border-gray-300 rounded"
            >
              <option value="">Apply Template</option>
              <option value="standard">Standard (Mon-Sat)</option>
              <option value="weekdays">Copy Mon to Weekdays</option>
              <option value="weekend">Copy Sat to Sun</option>
              <option value="24x7">24x7 Operation</option>
            </select>

            <label className="flex items-center text-sm">
              <input
                type="checkbox"
                checked={applyToAll}
                onChange={(e) => setApplyToAll(e.target.checked)}
                className="h-4 w-4 text-blue-600 rounded mr-2"
              />
              Apply changes to all days
            </label>
          </div>
        )}
      </div>

      {/* Working Hours Grid */}
      <div className="space-y-2">
        {days.map((day) => {
          const dayHours = hours[day];
          const isOpen = dayHours.isOpen;
          const hasBreak = dayHours.breakStart && dayHours.breakEnd;

          return (
            <div
              key={day}
              className={`flex items-center gap-4 p-4 rounded-lg border transition-all ${
                isToday(day)
                  ? "bg-blue-50 border-blue-200"
                  : "bg-gray-50 border-gray-200"
              } ${!isOpen ? "opacity-60" : ""}`}
            >
              {/* Day Name and Toggle */}
              <div className="w-32">
                <label className="flex items-center">
                  <input
                    type="checkbox"
                    checked={isOpen}
                    onChange={() => toggleDay(day)}
                    disabled={viewOnly}
                    className="h-4 w-4 text-blue-600 rounded mr-2"
                  />
                  <span
                    className={`font-medium ${
                      isToday(day) ? "text-blue-700" : ""
                    }`}
                  >
                    {getDayDisplayName(day)}
                    {isToday(day) && (
                      <span className="ml-1 text-xs text-blue-600">
                        (Today)
                      </span>
                    )}
                  </span>
                </label>
              </div>

              {isOpen ? (
                <>
                  {/* Working Hours */}
                  <div className="flex items-center gap-2">
                    <input
                      type="time"
                      value={dayHours.open}
                      onChange={(e) => updateDay(day, "open", e.target.value)}
                      disabled={viewOnly}
                      className="px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                    <span className="text-sm text-gray-500">to</span>
                    <input
                      type="time"
                      value={dayHours.close}
                      onChange={(e) => updateDay(day, "close", e.target.value)}
                      disabled={viewOnly}
                      className="px-2 py-1 border border-gray-300 rounded text-sm"
                    />
                  </div>

                  {/* Break Hours */}
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-gray-600">Break:</span>
                    <input
                      type="time"
                      value={dayHours.breakStart}
                      onChange={(e) =>
                        updateDay(day, "breakStart", e.target.value)
                      }
                      disabled={viewOnly}
                      className="px-2 py-1 border border-gray-300 rounded text-sm"
                      placeholder="Start"
                    />
                    {(dayHours.breakStart || dayHours.breakEnd) && (
                      <>
                        <span className="text-sm text-gray-500">to</span>
                        <input
                          type="time"
                          value={dayHours.breakEnd}
                          onChange={(e) =>
                            updateDay(day, "breakEnd", e.target.value)
                          }
                          disabled={viewOnly}
                          className="px-2 py-1 border border-gray-300 rounded text-sm"
                          placeholder="End"
                        />
                      </>
                    )}
                  </div>

                  {/* Copy Button */}
                  {!viewOnly && day !== "sunday" && (
                    <div className="flex-1 flex justify-end">
                      <div className="relative group">
                        <button
                          type="button"
                          className="p-1 text-gray-400 hover:text-blue-600"
                          title="Copy to other days"
                        >
                          <DuplicateIcon className="h-4 w-4" />
                        </button>
                        <div className="absolute right-0 mt-1 w-32 bg-white shadow-lg rounded-md hidden group-hover:block z-10">
                          {days
                            .filter((d) => d !== day)
                            .map((targetDay) => (
                              <button
                                key={targetDay}
                                type="button"
                                onClick={() => copyHours(day, targetDay)}
                                className="block w-full text-left px-3 py-1 text-sm hover:bg-gray-100"
                              >
                                Copy to {getDayDisplayName(targetDay)}
                              </button>
                            ))}
                        </div>
                      </div>
                    </div>
                  )}

                  {/* Status Indicator */}
                  <div className="flex items-center gap-2">
                    {hasBreak ? (
                      <span className="text-xs bg-yellow-100 text-yellow-700 px-2 py-1 rounded">
                        Has Break
                      </span>
                    ) : (
                      <span className="text-xs bg-green-100 text-green-700 px-2 py-1 rounded">
                        No Break
                      </span>
                    )}
                  </div>
                </>
              ) : (
                <div className="flex-1 flex items-center justify-between">
                  <span className="text-sm text-gray-500 italic">Closed</span>
                  {!viewOnly && (
                    <button
                      type="button"
                      onClick={() => toggleDay(day)}
                      className="text-xs text-blue-600 hover:text-blue-700"
                    >
                      Set as Open
                    </button>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Holiday Calendar Section */}
      <div className="border-t pt-6">
        <div className="flex items-center justify-between mb-4">
          <h3 className="text-lg font-semibold flex items-center">
            <CalendarIcon className="h-5 w-5 mr-2" />
            Holiday Calendar
          </h3>
          {!viewOnly && (
            <button
              type="button"
              onClick={() => setShowHolidayForm(true)}
              className="flex items-center gap-2 px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700 text-sm"
            >
              <PlusIcon className="h-4 w-4" />
              Add Holiday
            </button>
          )}
        </div>

        {/* Holiday Form */}
        {showHolidayForm && !viewOnly && (
          <div className="mb-4 p-4 bg-blue-50 rounded-lg">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Date
                </label>
                <input
                  type="date"
                  value={newHoliday.date}
                  onChange={(e) =>
                    setNewHoliday((prev) => ({ ...prev, date: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Holiday Name
                </label>
                <input
                  type="text"
                  value={newHoliday.name}
                  onChange={(e) =>
                    setNewHoliday((prev) => ({ ...prev, name: e.target.value }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                  placeholder="e.g., Diwali"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Status
                </label>
                <select
                  value={newHoliday.isOpen}
                  onChange={(e) =>
                    setNewHoliday((prev) => ({
                      ...prev,
                      isOpen: e.target.value === "true",
                    }))
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded"
                >
                  <option value="false">Closed</option>
                  <option value="true">Open with Special Hours</option>
                </select>
              </div>
              {newHoliday.isOpen && (
                <div className="grid grid-cols-2 gap-2">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Opens at
                    </label>
                    <input
                      type="time"
                      value={newHoliday.specialHours.open}
                      onChange={(e) =>
                        setNewHoliday((prev) => ({
                          ...prev,
                          specialHours: {
                            ...prev.specialHours,
                            open: e.target.value,
                          },
                        }))
                      }
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                    />
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Closes at
                    </label>
                    <input
                      type="time"
                      value={newHoliday.specialHours.close}
                      onChange={(e) =>
                        setNewHoliday((prev) => ({
                          ...prev,
                          specialHours: {
                            ...prev.specialHours,
                            close: e.target.value,
                          },
                        }))
                      }
                      className="w-full px-2 py-1 border border-gray-300 rounded"
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="flex justify-end gap-2 mt-4">
              <button
                type="button"
                onClick={() => {
                  setShowHolidayForm(false);
                  setNewHoliday({
                    date: "",
                    name: "",
                    isOpen: false,
                    specialHours: { open: "", close: "" },
                  });
                }}
                className="px-3 py-1 border border-gray-300 rounded hover:bg-gray-50"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={addHoliday}
                className="px-3 py-1 bg-blue-600 text-white rounded hover:bg-blue-700"
              >
                Add Holiday
              </button>
            </div>
          </div>
        )}

        {/* Holiday List */}
        {holidayList.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-3">
            {holidayList
              .sort((a, b) => new Date(a.date) - new Date(b.date))
              .map((holiday) => {
                const isUpcoming = new Date(holiday.date) > new Date();
                return (
                  <div
                    key={holiday.id}
                    className={`p-3 border rounded-lg ${
                      isUpcoming ? "bg-white" : "bg-gray-50 opacity-75"
                    }`}
                  >
                    <div className="flex justify-between items-start">
                      <div className="flex-1">
                        <p className="font-medium">{holiday.name}</p>
                        <p className="text-sm text-gray-600">
                          {new Date(holiday.date).toLocaleDateString("en-IN", {
                            weekday: "short",
                            day: "numeric",
                            month: "short",
                            year: "numeric",
                          })}
                        </p>
                        <div className="mt-1">
                          {holiday.isOpen ? (
                            <span className="inline-flex items-center gap-1 text-xs text-green-700">
                              <CheckIcon className="h-3 w-3" />
                              Open: {holiday.specialHours.open} -{" "}
                              {holiday.specialHours.close}
                            </span>
                          ) : (
                            <span className="inline-flex items-center gap-1 text-xs text-red-700">
                              <XIcon className="h-3 w-3" />
                              Closed
                            </span>
                          )}
                        </div>
                      </div>
                      {!viewOnly && (
                        <button
                          type="button"
                          onClick={() => removeHoliday(holiday.id)}
                          className="p-1 text-red-600 hover:bg-red-50 rounded"
                        >
                          <TrashIcon className="h-4 w-4" />
                        </button>
                      )}
                    </div>
                  </div>
                );
              })}
          </div>
        ) : (
          <div className="text-center py-8 text-gray-500 bg-gray-50 rounded-lg">
            <CalendarIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
            <p>No holidays configured</p>
            {!viewOnly && (
              <p className="text-sm mt-1">
                Click "Add Holiday" to configure holidays
              </p>
            )}
          </div>
        )}
      </div>

      {/* Summary */}
      <div className="bg-blue-50 p-4 rounded-lg">
        <h4 className="font-medium mb-2">Working Hours Summary</h4>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
          <div>
            <span className="text-gray-600">Open Days:</span>
            <p className="font-medium">
              {days.filter((d) => hours[d].isOpen).length}/7 days
            </p>
          </div>
          <div>
            <span className="text-gray-600">Total Holidays:</span>
            <p className="font-medium">{holidayList.length}</p>
          </div>
          <div>
            <span className="text-gray-600">Days with Breaks:</span>
            <p className="font-medium">
              {
                days.filter((d) => hours[d].breakStart && hours[d].breakEnd)
                  .length
              }
            </p>
          </div>
          <div>
            <span className="text-gray-600">Upcoming Holidays:</span>
            <p className="font-medium">
              {holidayList.filter((h) => new Date(h.date) > new Date()).length}
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};

export default WorkingHoursConfig;
